import { ClearOutlined, SearchOutlined } from '@mui/icons-material'
import { IconButton, InputBase, InputBaseProps } from '@mui/material'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { SeleniumProperty } from '@/types/seleniumProperty'

/**
 * Displayes a search field, complete with a reset button.
 */
export const SearchField: React.FC<
    Pick<
        InputBaseProps,
        'value' | 'onChange' | 'fullWidth' | 'disabled' | 'style' | 'sx'
    > &
        SeleniumProperty & { onClickReset?: React.MouseEventHandler }
> = ({ sx, onChange, onClickReset, disabled, ...props }) => {
    const ref = React.useRef<HTMLInputElement>(null)
    const { t } = useTranslation()

    return (
        <InputBase
            ref={ref}
            {...props}
            onChange={onChange}
            placeholder={t('common.search')}
            disabled={disabled}
            startAdornment={
                <SearchOutlined
                    onClick={() => ref.current?.querySelector('input')?.focus()}
                />
            }
            endAdornment={
                <IconButton
                    size={'small'}
                    onClick={onClickReset}
                    disabled={disabled}
                >
                    <ClearOutlined />
                </IconButton>
            }
            sx={[
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                ...(Array.isArray(sx) ? sx : [sx]),
                theme => ({
                    padding: 1,
                    gap: 1,
                    borderRadius: 1,

                    ...theme.applyStyles('dark', {
                        backgroundColor: `rgb(from ${theme.palette.common.white} r g b / 0.25)`,

                        '&:hover, &:focus-within': {
                            backgroundColor: `rgb(from ${theme.palette.common.white} r g b / 0.3)`,
                        },
                    }),

                    ...theme.applyStyles('light', {
                        backgroundColor: `rgb(from ${theme.palette.common.black} r g b / 0.1)`,

                        '&:hover, &:focus-within': {
                            backgroundColor: `rgb(from ${theme.palette.common.black} r g b / 0.05)`,
                        },
                    }),
                }),
            ]}
        />
    )
}
